
///
/// EarthBrowser - Permission Hooks
///
/// Created: 2025-11-15
/// Copyright (c) 2025 Geodata Labs
///

import { useMemo } from "react";
import { useGetUserPermissionsQuery } from "@/redux/permissionsApi";

/**
 * Hook to get current user's permissions
 */
export function usePermissions() {
	return useGetUserPermissionsQuery();
}

/**
 * Hook to check if user has a specific permission
 */
export function useHasPermission(permission: string): boolean {
	const { data } = useGetUserPermissionsQuery();
	return useMemo(
		() =>
			data?.permissions.some((p) => p.permission_name === permission) ?? false,
		[data, permission]
	);
}

/**
 * Hook to check if user has any of the specified permissions
 */
export function useHasAnyPermission(permissions: string[]): boolean {
	const { data } = useGetUserPermissionsQuery();
	return useMemo(
		() =>
			permissions.some((permission) =>
				data?.permissions.some((p) => p.permission_name === permission)
			) ?? false,
		[data, permissions]
	);
}

/**
 * Hook to check if user has all of the specified permissions
 */
export function useHasAllPermissions(permissions: string[]): boolean {
	const { data } = useGetUserPermissionsQuery();
	return useMemo(
		() =>
			permissions.every((permission) =>
				data?.permissions.some((p) => p.permission_name === permission)
			) ?? false,
		[data, permissions]
	);
}

/**
 * Hook to check if user has a specific role
 */
export function useHasRole(roleName: string): boolean {
	const { data } = useGetUserPermissionsQuery();
	return useMemo(
		() => data?.roles.some((r) => r.name === roleName) ?? false,
		[data, roleName]
	);
}
